import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Tabs, Tab, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  Chip, CircularProgress, Stack, Select, MenuItem, FormControl, InputLabel, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { categoryTaxonomyApi } from '../../services/api';
import UnmappedCategoryTypesPanel from './UnmappedCategoryTypesPanel';
import AddTypeDialog from './AddTypeDialog';

function CategoryTypesPanel({ families, onChange }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [filterFamily, setFilterFamily] = useState('');
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await categoryTaxonomyApi.listTypes();
      setTypes(resp.types || []);
    } catch (e) {
      setError(e.message || 'Failed to load category types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReassign = async (category, familyId) => {
    try {
      setError(null);
      setInfo(null);
      await categoryTaxonomyApi.updateTypeFamily(category, familyId);
      setInfo(`Reassigned "${category}" to new family`);
      await load();
      if (onChange) onChange();
    } catch (e) {
      setError(e.message || 'Failed to reassign');
    }
  };

  const filtered = types.filter(t => {
    if (filterFamily && t.family_id !== filterFamily) return false;
    if (search && !t.category.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Family</InputLabel>
          <Select value={filterFamily} label="Family" onChange={(e) => setFilterFamily(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            {families.map(f => <MenuItem key={f.id} value={f.id}>{f.family}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="Category contains..." value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240 }} />
        {(filterFamily || search) && <Button size="small" onClick={() => { setFilterFamily(''); setSearch(''); }}>Clear</Button>}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {filtered.length} of {types.length} category types
        </Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add type</Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        The closed list of category types. The AI maps every extracted category to one of these,
        and matching gates on the family, so one name per garment type.
      </Alert>

      <AddTypeDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        kind="category"
        existing={types.map(t => ({ value: t.category, family: (families.find(f => f.id === t.family_id) || {}).family || null }))}
        families={families.map(f => ({ key: f.id, label: f.family }))}
        onCreate={async (category, familyId) => {
          await categoryTaxonomyApi.createType(category, familyId);
          setInfo(`Added category type "${category}"`);
          await load();
          if (onChange) onChange();
        }}
      />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Category (normalized_category)</strong></TableCell>
              <TableCell><strong>Display name</strong></TableCell>
              <TableCell width={260}><strong>Family</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(t => (
              <TableRow key={t.id}>
                <TableCell><Chip label={t.category} size="small" variant="outlined" /></TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{t.display_name || '—'}</Typography></TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={t.family_id || ''}
                      onChange={(e) => handleReassign(t.category, e.target.value)}
                      displayEmpty
                    >
                      {!t.family_id && <MenuItem value=""><em>(unassigned)</em></MenuItem>}
                      {families.map(f => <MenuItem key={f.id} value={f.id}>{f.family}</MenuItem>)}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={3} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No category types match filters.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function FamiliesPanel({ onChange }) {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ family: '', description: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await categoryTaxonomyApi.listFamilies();
      setFamilies(resp.families || []);
    } catch (e) {
      setError(e.message || 'Failed to load families');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpenAdd = () => {
    setEditing(null);
    setFormData({ family: '', description: '' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (f) => {
    setEditing(f);
    setFormData({ family: f.family, description: f.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      if (editing) {
        await categoryTaxonomyApi.updateFamily(editing.id, formData);
      } else {
        await categoryTaxonomyApi.createFamily(formData);
      }
      setDialogOpen(false);
      await load();
      if (onChange) onChange();
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (f) => {
    if (f.category_type_count > 0) {
      window.alert(
        `Cannot delete "${f.family}" — ${f.category_type_count} category types still reference it. ` +
        `Reassign them to another family first (in the Category Types sub-tab).`
      );
      return;
    }
    if (!window.confirm(`Delete family "${f.family}"?`)) return;
    try {
      setError(null);
      await categoryTaxonomyApi.deleteFamily(f.id);
      await load();
      if (onChange) onChange();
    } catch (e) {
      setError(e.message || 'Failed to delete');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Top-level taxonomy (level 1). Used by size schemas and the embedding pipeline.
          Adding a family here makes it available in the Size Schemas → Applicable Families dropdown.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>Add Family</Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Family</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell align="right"><strong>Category types</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {families.map(f => (
              <TableRow key={f.id}>
                <TableCell><Chip label={f.family} size="small" color="primary" /></TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{f.description || '—'}</Typography></TableCell>
                <TableCell align="right">{f.category_type_count}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenEdit(f)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleDelete(f)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Family' : 'Add Family'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Family name"
            value={formData.family}
            onChange={(e) => setFormData({ ...formData, family: e.target.value })}
            fullWidth margin="normal"
            helperText="Lowercase, e.g. tops, knitwear, bottoms"
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth margin="normal" multiline rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !formData.family}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function CategoriesTab() {
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [families, setFamilies] = useState([]);

  const loadFamilies = useCallback(async () => {
    try {
      const resp = await categoryTaxonomyApi.listFamilies();
      setFamilies(resp.families || []);
    } catch (e) {
      // best-effort; downstream panel handles errors
    }
  }, []);

  useEffect(() => { loadFamilies(); }, [loadFamilies]);

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeSubTab} onChange={(_e, v) => setActiveSubTab(v)}>
          <Tab label="Unmapped Category Types" />
          <Tab label="Category Types → Family" />
          <Tab label="Category Families" />
        </Tabs>
      </Box>
      {activeSubTab === 0 && <UnmappedCategoryTypesPanel />}
      {activeSubTab === 1 && <CategoryTypesPanel families={families} onChange={loadFamilies} />}
      {activeSubTab === 2 && <FamiliesPanel onChange={loadFamilies} />}
    </Box>
  );
}
